import objectFitImages from 'object-fit-images';
import ImageBuddy from 'imagebuddy';

import ThemeWelcome from './components/testing/theme-welcome';
import SiteHeader from './components/site-header';
import Modal from './components/modal';

import PostsListBlog from './components/posts-list/posts-list-blog';
import PostsListSearch from './components/posts-list/posts-list-search';

import DocumentariesTemplate from './templates/documentaries';

import { processExternalLinks } from './util/general-util';
import ScrollWatcher from './util/scroll-watcher';
import SiteEvents, { SiteEventNames } from './util/site-events';
import { loadIntersectionObserver } from './util/load-dependencies';
import { createSingleUseObserver } from './util/intersection-observer';
import { initHelloBar } from './util/hello-bar';
import ModalDefault from './components/modal-default';
import EqualHeight from './util/equal-height';

import LogosSlider from './components/logos-silder';

import TestimonialsSlider from './components/testimonials-slider';
import StatsSlider from './components/stats-slider';
import SocialShare from './components/social-share';
import Accordion from './components/accordion';
import TabbedContent from './components/tabbed-content';
import NewsletterSubmit from './components/newsletter-submit';
import MediaContentSlider from './components/media-content-slider';
import ImageGallerySlider from './components/image-gallery-slider';
import FilmSlider from './components/film-slider';
import EpisodeSlider from './components/episode-slider';
import ComingSoon from './templates/coming-soon';

import AccordionImage from './blocks/accordion-image';

class App {
	constructor() {
		this.instances = {
			components: {
				accordion: [],
				episodeSlider: [],
				equalHeight: [],
				filmSlider: [],
				imageGallerySlider: [],
				logosSlider: [],
				mediaContentSlider: [],
				modal: [],
				modalVideoPopup: [],
				newsletterSubmit: [],
				postsListBlog: [],
				postsListSearch: [],
				siteHeader: null,
				socialShare: null,
				statsSlider: [],
				testimonialsSlider: [],
				themeWelcome: null
			},
			templates: {
				documentariesTemplate: null,
				comingSoonTemplate: null
			},
			blocks: {
				accordionImage: [],
				tabbedContent: []
			},
			scrollWatcher: null,
			imageBuddy: null
		};

		this.init();
		this.initComponents();
		this.initTemplates();
		this.initBlocks();
		this.initSocialShare();
	}

	init() {
		processExternalLinks({
			target: '_blank',
			rel: 'noopener'
		});

		Modal.setDefaults({
			closeDuration: 400,
			closeButtonContent: `
				<svg xmlns="http://www.w3.org/2000/svg" class="modal__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			`.trim()
		});

		this.scrollWatcher = new ScrollWatcher((params) => {
			ScrollWatcher.defaultCallback(params, 100);
		});

		// initialize ImageBuddy and setup events
		this.instances.imageBuddy = new ImageBuddy({
			lazyLoad: true
			// debug: true
		});

		ImageBuddy.on('image-loaded', (imgEl) => {
			objectFitImages(imgEl);
		});

		SiteEvents.subscribe(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE, (opts) => {
			this.instances.imageBuddy.update(opts || {});
		});

		initHelloBar((hbEl) => {
			document.body.style.marginTop = `${hbEl.offsetHeight}px`;

			const siteHeaderEl = document.querySelector('.site-header');

			if (siteHeaderEl) {
				siteHeaderEl.style.marginTop = `${hbEl.offsetHeight}px`;
			}
		});

		// ObjectFitImages: only act upon images that have a valid src attribute
		// Note: use the 'object-fit-polyfill' SCSS mixin to create the necessary 'font-family' attribute
		objectFitImages('img:not([src^="data:"])');
	}

	async initComponents() {
		// theme welcome
		// TODO: remove during development
		const themeWelcomeEl = document.querySelector('.theme-welcome');

		if (themeWelcomeEl) {
			this.instances.themeWelcome = new ThemeWelcome(themeWelcomeEl);
		}

		// site header
		const siteHeaderEl = document.querySelector('.site-header');

		if (siteHeaderEl) {
			this.instances.components.siteHeader = new SiteHeader(siteHeaderEl);
		}

		// newsletter signup
		Array.from(document.querySelectorAll('.newsletter-signup')).forEach(
			(newsletterSignupEl) => {
				this.instances.components.newsletterSubmit.push(
					new NewsletterSubmit(newsletterSignupEl)
				);
			}
		);

		// posts list - blog
		Array.from(document.querySelectorAll('.posts-list--blog')).forEach((postsListBlogEl) => {
			this.instances.components.postsListBlog.push(new PostsListBlog(postsListBlogEl));
		});

		// posts list - search
		Array.from(document.querySelectorAll('.posts-list--search')).forEach(
			(postsListSearchEl) => {
				this.instances.components.postsListSearch.push(
					new PostsListSearch(postsListSearchEl)
				);
			}
		);

		// Default Modal
		Array.from(document.querySelectorAll('.js__modal--default')).forEach((modalDefaultEl) => {
			this.instances.components.modal.push(new ModalDefault(modalDefaultEl));
		});

		// Testimonials Slider
		Array.from(document.querySelectorAll('.block-testimonials')).forEach(
			(testimonialsBlock) => {
				this.slider = testimonialsBlock.querySelector(
					'.block-testimonials__testimonials-slider'
				);
				this.prevButton = testimonialsBlock.querySelector('.custom-prev-button');
				this.nextButton = testimonialsBlock.querySelector('.custom-next-button');
				if (this.slider) {
					this.instances.components.testimonialsSlider.push(
						new TestimonialsSlider(this.slider, this.prevButton, this.nextButton)
					);
				}
			}
		);

		// Image Gallery Slider
		Array.from(document.querySelectorAll('.block-image-gallery')).forEach(
			(imageGalleryBlock) => {
				this.slider = imageGalleryBlock.querySelector('.block-image-gallery__slider');
				this.prevButton = imageGalleryBlock.querySelector('.custom-prev-button');
				this.nextButton = imageGalleryBlock.querySelector('.custom-next-button');
				this.nav = imageGalleryBlock.querySelector('.custom-nav-container-image-gallery');
				if (this.slider) {
					this.instances.components.imageGallerySlider.push(
						new ImageGallerySlider(
							this.slider,
							this.prevButton,
							this.nextButton,
							this.nav
						)
					);
				}
			}
		);

		// Logos Slider
		Array.from(document.querySelectorAll('.block-logos')).forEach((logosBlock) => {
			this.slider = logosBlock.querySelector('.block-logos__logos-slider');
			if (this.slider) {
				this.instances.components.logosSlider.push(new LogosSlider(this.slider));
			}
		});

		// Film Slider
		Array.from(document.querySelectorAll('.component-film-slider')).forEach(
			(filmSliderComponent) => {
				this.slider = filmSliderComponent.querySelector(
					'.component-film-slider__film-slider'
				);
				this.loadingSpinner = filmSliderComponent.querySelector('.ui__spin-loader') || null;
				if (this.slider) {
					this.instances.components.filmSlider.push(
						new FilmSlider(this.slider, this.loadingSpinner)
					);
				}
			}
		);

		// Episode Slider
		Array.from(document.querySelectorAll('.tmpl-single-film__episode-slider')).forEach(
			(episodeSlider) => {
				this.instances.components.episodeSlider.push(new EpisodeSlider(episodeSlider));
			}
		);

		// Episode Slider
		Array.from(document.querySelectorAll('.tmpl-single-film__episode-slider-wrapper')).forEach(
			(episodeSliderBlock) => {
				this.slider = episodeSliderBlock.querySelector('.tmpl-single-film__episode-slider');

				if (this.slider) {
					this.instances.components.filmSlider.push(new FilmSlider(this.slider, 2));
				}
			}
		);

		// Media Content Slider
		Array.from(document.querySelectorAll('.block-media-content__slider-wrapper')).forEach(
			(mediaContentSlider) => {
				this.slider = mediaContentSlider.querySelector('.block-media-content__slider');
				this.instances.components.mediaContentSlider.push(
					new MediaContentSlider(this.slider)
				);
			}
		);

		// Social Share
		const socialShareEl = document.querySelector('.tmpl-single');

		if (socialShareEl) {
			this.instances.components.socialShare = new SocialShare(socialShareEl);
		}

		// accordion
		Array.from(document.querySelectorAll('.accordion-container')).forEach((accordionEl) => {
			this.instances.components.accordion.push(new Accordion(accordionEl));
		});

		// Stats Slider
		Array.from(document.querySelectorAll('.block-stats')).forEach((statsBlock) => {
			this.slider = statsBlock.querySelector('.stats__stat-slider');
			this.nav = statsBlock.querySelector('.custom-nav-container-stats');
			if (this.slider) {
				this.instances.components.statsSlider.push(new StatsSlider(this.slider, this.nav));
			}
		});

		// Equalize Height
		Array.from(document.querySelectorAll('.equal-height')).forEach((equalHeightContainer) => {
			this.instances.components.equalHeight.push(new EqualHeight(equalHeightContainer));
		});
	}

	initTemplates() {
		const documentariesTemplate = document.querySelector('.tmpl-documentaries');

		if (documentariesTemplate) {
			this.instances.templates.documentariesTemplate = new DocumentariesTemplate();
		}

		const comingSoonTemplate = document.querySelector('.tmpl-coming-soon');

		if (comingSoonTemplate) {
			this.instances.templates.comingSoonTemplate = new ComingSoon();
		}
	}

	async initBlocks() {
		// Accordion Image
		Array.from(document.querySelectorAll('.block-accordion')).forEach((accordionImage) => {
			this.instances.blocks.accordionImage.push(new AccordionImage(accordionImage));
		});

		// Tabbed Content
		Array.from(document.querySelectorAll('.block-tabbed-content')).forEach((tabbedContent) => {
			this.instances.blocks.tabbedContent.push(new TabbedContent(tabbedContent));
		});

		// setup block animation watchers
		const animateBlocks = document.querySelectorAll('.block-animate');

		if (animateBlocks && animateBlocks.length) {
			try {
				await loadIntersectionObserver();

				animateBlocks.forEach((watchEl) =>
					createSingleUseObserver(watchEl, (entry, el) => {
						el.classList.remove('block-animate');
					})
				);
			} catch (err) {
				console.error(err);
			}
		}
	}

	initSocialShare() {
		const socialShareEls = document.querySelectorAll('[data-social-share]');

		Array.from(socialShareEls).forEach((el) => {
			el.addEventListener('click', (e) => {
				const site = el.getAttribute('data-social-share');
				const shareUrl = el.getAttribute('href');

				e.preventDefault();

				if (!shareUrl) {
					return;
				}

				window.open(shareUrl, `${site}Share`, 'width=626,height=436');
			});
		});
	}
}

export default App;
